import { useEffect, useId } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface ScheduleFeedbackItem {
  id: string;
  briefing_id: string;
  author_name: string;
  body: string;
  event_date: string | null;
  crew_member_id: string | null;
  stage_name: string | null;
  anchor_time: string | null;
  artist_name: string | null;
  status: "open" | "resolved" | string;
  is_read: boolean;
  created_at: string;
  // Embedded crew member name (via FK), when the comment targets one.
  crew_members?: { name: string } | null;
}

/**
 * Single source of truth for client schedule feedback on a production. Powers
 * three places at once (so the counts stay consistent): the "Schedule" nav badge
 * (unread = new), the feedback panel on the schedule page, and the Producer
 * Dashboard "open feedback" widget. Live-updates via the existing Realtime
 * postgres_changes pattern.
 */
export function useScheduleFeedback(productionId: string | undefined) {
  const queryClient = useQueryClient();
  const queryKey = ["schedule-feedback", productionId];
  // Unique per hook instance so several mounts (nav badge, schedule panel,
  // dashboard widget) don't collide on the same Realtime channel name.
  const instanceId = useId();

  const { data: feedback = [], isLoading } = useQuery({
    queryKey,
    enabled: !!productionId,
    queryFn: async (): Promise<ScheduleFeedbackItem[]> => {
      const { data, error } = await supabase
        .from("schedule_feedback")
        .select("*, crew_members(name)")
        .eq("briefing_id", productionId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as ScheduleFeedbackItem[];
    },
  });

  // Live badge/widget updates: any insert/update on this briefing's feedback
  // refetches. Subscribers only refetch (never read the OLD payload), matching
  // the repo's realtime convention.
  useEffect(() => {
    if (!productionId) return;
    const channel = supabase
      .channel(`schedule-feedback-${productionId}-${instanceId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "schedule_feedback",
          filter: `briefing_id=eq.${productionId}`,
        },
        () => queryClient.invalidateQueries({ queryKey }),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productionId]);

  const updateStatus = useMutation({
    mutationFn: async ({
      id,
      status,
      is_read,
    }: {
      id: string;
      status?: "open" | "resolved";
      is_read?: boolean;
    }) => {
      const patch: Record<string, unknown> = {};
      if (status !== undefined) patch.status = status;
      if (is_read !== undefined) patch.is_read = is_read;
      const { error } = await supabase.from("schedule_feedback").update(patch).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  const removeFeedback = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("schedule_feedback").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  const openItems = feedback.filter((f) => f.status === "open");
  const unreadCount = feedback.filter((f) => !f.is_read).length;

  return {
    feedback,
    openItems,
    openCount: openItems.length,
    unreadCount,
    isLoading,
    markRead: (id: string) => updateStatus.mutate({ id, is_read: true }),
    resolve: (id: string) => updateStatus.mutate({ id, status: "resolved", is_read: true }),
    reopen: (id: string) => updateStatus.mutate({ id, status: "open" }),
    remove: (id: string) => removeFeedback.mutate(id),
  };
}
